// components/layout/NavMenu/hooks/useNavSubscriptions.ts
/**
 * Nav subscriptions — the always-on data the nav needs regardless
 * of permissions. Splitting permission-gated stuff into its own
 * hook (useNavBadgeCounts) lets NavMenu call THIS one exactly
 * once: previously the chicken-and-egg between useNavPermissions
 * (needs isActingLeadFromTeams) and the old all-in-one
 * useNavSubscriptions (needed permissions) forced NavMenu to mount
 * the hook twice, doubling every listener + Firebase fetch this
 * hook owns.
 *
 * What stays here:
 *   - Tournament metadata fetch (one-time, filtered to the
 *     viewer)
 *   - onBreak listener (single field, single path)
 *   - isActingLeadFromTeams (computed from tournament floorTeams
 *     + a small set of team-lead onBreak watchers)
 *   - engineTournaments (pure derive from tournaments)
 *
 * What lives in useNavBadgeCounts:
 *   - pendingAppeals polling
 *   - pendingUsers query subscription
 *   - deckCheckTournaments derive (it gates on permissions)
 *
 * Net: one fetch + one onBreak listener + one acting-lead block
 * per NavMenu mount, instead of two.
 */
import { useState, useEffect, useMemo } from 'react';
import { ref, onValue, Unsubscribe } from 'firebase/database';
import { db } from '../../../../config/firebase';
import type { UserData } from '../../../../context/AuthContext';
import { useTournamentMetadata } from '../../../../hooks/useTournamentMetadata';

// Tournament type for nav purposes - only metadata needed
export interface NavTournament {
  id: string;
  name?: string;
  status?: string;
  isPublic?: boolean;
  staffAssignments?: Record<string, unknown>;
  allowedRoles?: string[];
  ownerUid?: string;
  ownerStoreId?: string;
  engine?: {
    settings?: {
      format?: string;
    };
  };
  floorTeams?: Record<string, {
    teamLeadId?: string;
    assistantId?: string;
  }>;
}

export interface NavSubscriptionsInput {
  user: UserData | null;
}

export interface NavSubscriptionsResult {
  tournaments: NavTournament[];
  engineTournaments: NavTournament[];
  onBreak: boolean;
  isActingLeadFromTeams: boolean;
}

export function useNavSubscriptions({ user }: NavSubscriptionsInput): NavSubscriptionsResult {
  const { tournaments: metadataRows } = useTournamentMetadata();
  const [onBreak, setOnBreak] = useState(false);
  const [isActingLeadFromTeams, setIsActingLeadFromTeams] = useState(false);

  const role = user?.role || '';
  const roleLower = role.toLowerCase();
  const accountType = user?.accountType;

  // Determines who sees all tournaments
  const hasFullAccess = accountType === 'admin' || ['admin', 'manager', 'scorekeeper'].includes(roleLower);

  // Filter tournaments visible to this user
  const filterVisible = (allTournaments: NavTournament[]): NavTournament[] => {
    if (hasFullAccess) return allTournaments;

    // Store owners see only their own tournaments
    if (accountType === 'store') {
      return allTournaments.filter((t) => {
        if (t.ownerUid === user?.uid) return true;
        const storeId = (user as Record<string, unknown>)?.storeId as string | undefined;
        if (t.ownerStoreId && storeId && t.ownerStoreId === storeId) return true;
        if (t.staffAssignments?.[user?.uid || '']) return true;
        return false;
      });
    }

    // Staff and other roles see assigned tournaments
    return allTournaments.filter((t) => {
      if (t.isPublic) return true;
      if (t.staffAssignments?.[user?.uid || '']) return true;
      if (t.allowedRoles?.includes(role)) return true;
      // Check user's own assignment data (always up-to-date, unlike metadata)
      if (user?.tournamentAssignments?.[t.id]) return true;
      if (user?.floorTeamAssignments?.[t.id]) return true;
      return false;
    });
  };

  // useTournamentMetadata gives us the orphan-filtered, active-only
  // rows. Visibility filter runs in a memo so re-renders driven by
  // user/role changes don't re-fetch — the previous effect did.
  const tournaments = useMemo<NavTournament[]>(() => {
    const allTournaments: NavTournament[] = metadataRows.map((m) => ({
      id: m.id,
      name: m.name,
      status: m.status,
      isPublic: m.isPublic,
      staffAssignments: m.staffAssignments,
      allowedRoles: m.allowedRoles,
      ownerUid: m.ownerUid,
      ownerStoreId: m.ownerStoreId,
      engine: m.format ? { settings: { format: m.format } } : undefined,
      floorTeams: m.floorTeams as NavTournament['floorTeams'],
    }));
    return filterVisible(allTournaments);
  // filterVisible closes over user/role/accountType; listing those
  // explicitly so the memo invalidates on the same triggers the
  // old effect re-ran on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataRows, user, role, roleLower, accountType]);

  // GRANULAR: Subscribe only to onBreak field, not entire user record
  useEffect(() => {
    if (!user?.uid) return;
    const onBreakRef = ref(db, `users/${user.uid}/onBreak`);
    const unsubscribe = onValue(onBreakRef, (snapshot) => {
      setOnBreak(snapshot.val() === true);
    });
    return () => unsubscribe();
  }, [user?.uid]);

  // Check if user is acting lead based on floor team data
  useEffect(() => {
    if (!user?.uid || !tournaments.length) {
      setIsActingLeadFromTeams(false);
      return;
    }

    const teamLeadIds = new Set<string>();
    tournaments.forEach(t => {
      const floorTeams = t.floorTeams || {};
      Object.values(floorTeams).forEach(team => {
        if (team.assistantId === user.uid && team.teamLeadId) {
          teamLeadIds.add(team.teamLeadId);
        }
      });
    });

    if (teamLeadIds.size === 0) {
      setIsActingLeadFromTeams(false);
      return;
    }

    const breakStatuses = new Map<string, boolean>();
    const unsubscribes: Unsubscribe[] = [];

    const updateActingStatus = () => {
      const anyOnBreak = Array.from(breakStatuses.values()).some(status => status === true);
      setIsActingLeadFromTeams(anyOnBreak);
    };

    teamLeadIds.forEach(leadId => {
      const leadRef = ref(db, `users/${leadId}/onBreak`);
      const unsub = onValue(leadRef, (snapshot) => {
        breakStatuses.set(leadId, snapshot.val() === true);
        updateActingStatus();
      });
      unsubscribes.push(unsub);
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [user?.uid, tournaments]);

  // Filter tournaments for engine mode (no permissions dependency)
  const engineTournaments = useMemo(() => {
    return tournaments.filter(t => t.engine?.settings?.format);
  }, [tournaments]);

  return {
    tournaments,
    engineTournaments,
    onBreak,
    isActingLeadFromTeams,
  };
}

export default useNavSubscriptions;
